import React, { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import { FloatingHScroll } from './FloatingHScroll';
import { LeadsNova72hBlockingPopup } from './LeadsNova72hBlockingPopup';
import { PushEnableBanner } from './PushEnableBanner';
import { PushPrefsDialog } from './PushPrefsDialog';
import { playChatSound } from './chat/chatSound';
import { AnnouncementPopup } from './chat/AnnouncementPopup';
import { AvatarReminder } from './AvatarReminder';
import { ChatDock } from './chat/ChatDock';
import { avatarSrc } from '../lib/avatar';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { BackButton } from './BackButton';
import {
  LayoutDashboard,
  Users,
  UserCog,
  UsersRound,
  Ticket,
  Calendar,
  Mail,
  Phone,
  Video,
  BookOpen,
  Settings,
  LogOut,
  HelpCircle,
  Menu,
  Bell,
  Search,
  Plus,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  RefreshCw,
  Zap,
  Sun,
  Plug,
  Tv,
  Shield,
  Flame,
  BatteryCharging,
  MoreHorizontal,
  ClipboardList,
  Handshake,
  FileText,
  AlertTriangle,
  Trophy,
  XCircle,
  X,
  Clock,
  Link2,
  UserPlus,
  FileCheck,
  ArrowRightLeft,
  UserCheck,
  BarChart3,
  Gauge,
  Target,
  MessageSquare,
} from 'lucide-react';
import { Wallet, Upload, Lightbulb, Wrench, TrendingUp, CalendarClock, CalendarDays, ShieldCheck, Database, Calculator, BellRing } from 'lucide-react';
import { StrongNotificationPopup } from './StrongNotificationPopup';
import { AdminAlertBlockingPopup } from './AdminAlertBlockingPopup';
import { PendenciaBlockingPopup } from './pendencias/PendenciaBlockingPopup';
import { LeadAssignBlockingPopup } from './LeadAssignBlockingPopup';
import { QuickReassignDialog } from './QuickReassignDialog';
import { SendAlertModal } from './SendAlertModal';
import { RecognitionBlockingPopup } from './RecognitionBlockingPopup';
import { SendRecognitionModal } from './SendRecognitionModal';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from './ui/collapsible';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_utility-finder-7/artifacts/wusjk2bl_1.png";

// Company structure with colored icons
const CRM_STRUCTURE = {
  energias: {
    label: 'Energias',
    icon: Zap,
    iconColor: 'text-yellow-400',
    bgColor: 'bg-yellow-400/20',
    subcategories: {
      luz_gas: {
        label: 'Luz e Gás',
        icon: Flame,
        iconColor: 'text-orange-400',
        companies: [
          { key: 'edp', name: 'EDP' },
          { key: 'galp', name: 'Galp' },
          { key: 'endesa', name: 'Endesa' },
          { key: 'iberdrola', name: 'Iberdrola' },
          { key: 'gold', name: 'Gold Energy' },
          { key: 'repsol', name: 'Repsol' },
          { key: 'yes', name: 'Yes' },
          { key: 'meo_energia', name: 'Meo Energia' },
        ]
      },
      solar: {
        label: 'Solar',
        icon: Sun,
        iconColor: 'text-amber-400',
        companies: [
          { key: 'edp', name: 'EDP' },
          { key: 'galp', name: 'Galp' },
          { key: 'iberdrola', name: 'Iberdrola' },
        ]
      },
      carregamento: {
        label: 'Carregamento Elétrico',
        icon: BatteryCharging,
        iconColor: 'text-green-400',
        companies: [
          { key: 'edp', name: 'EDP' },
          { key: 'galp', name: 'Galp' },
          { key: 'iberdrola', name: 'Iberdrola' },
        ]
      }
    }
  },
  telecomunicacoes: {
    label: 'Telecomunicações',
    icon: Tv,
    iconColor: 'text-purple-400',
    bgColor: 'bg-purple-400/20',
    companies: [
      { key: 'nos', name: 'NOS' },
      { key: 'meo', name: 'MEO' },
      { key: 'vodafone', name: 'Vodafone' },
    ]
  },
  alarmes: {
    label: 'Alarmes',
    icon: Shield,
    iconColor: 'text-red-400',
    bgColor: 'bg-red-400/20',
    companies: [
      { key: 'nos_securitas', name: 'NOS Securitas' },
      { key: 'prosegur', name: 'Prosegur' },
    ]
  }
};

const supportSection = [
  { label: 'Tickets', icon: Ticket, iconColor: 'text-cyan-400', path: '/tickets' },
  { label: 'Base de Conhecimento', icon: BookOpen, iconColor: 'text-indigo-400', path: '/knowledge' },
];

const productionSection = [
  { label: 'Relatórios Produção', icon: TrendingUp, iconColor: 'text-emerald-400', path: '/dashboard/relatorios' },
  { label: 'Prod. Parceiros', icon: Handshake, iconColor: 'text-orange-400', path: '/dashboard/parceiros' },
  { label: 'Prod. Comerciais', icon: Users, iconColor: 'text-sky-400', path: '/dashboard/comerciais' },
  { label: 'Prod. Angariadores', icon: UserCheck, iconColor: 'text-teal-400', path: '/dashboard/angariadores' },
];

const leadsSection = [
  { label: 'Minha Pipeline', icon: Phone, iconColor: 'text-cyan-400', path: '/leads/pipeline' },
  { label: 'Importar Leads', icon: Upload, iconColor: 'text-fuchsia-400', path: '/leads/importar', allowedRoles: ['super_admin', 'gestor', 'admin', 'parceiro'], hideForLeadGeneratorOnly: true },
  { label: 'Performance Comerciais', icon: BarChart3, iconColor: 'text-indigo-400', path: '/leads/performance', allowedRoles: ['super_admin', 'gestor', 'admin', 'parceiro', 'backoffice', 'chefe_equipa'] },
  { label: 'Gestão de Leads', icon: Gauge, iconColor: 'text-rose-400', path: '/leads/gestao', allowedRoles: ['super_admin', 'gestor'] },
  { label: 'Relatório Diário', icon: CalendarDays, iconColor: 'text-emerald-400', path: '/leads/relatorio-diario', allowedRoles: ['super_admin', 'gestor'] },
  { label: 'Performance', icon: TrendingUp, iconColor: 'text-amber-400', path: '/leads/stats', allowedRoles: ['super_admin', 'gestor', 'admin', 'parceiro', 'backoffice', 'chefe_equipa', 'angariador'] },
  { label: 'Follow-ups 1ª Fatura', icon: CalendarClock, iconColor: 'text-rose-400', path: '/leads/follow-ups-fatura', hideForLeadGeneratorOnly: true },
  { label: 'Check-ins D+45', icon: ShieldCheck, iconColor: 'text-teal-400', path: '/leads/follow-ups-d45', hideForLeadGeneratorOnly: true },
  { label: 'Leads Externas', icon: Link2, iconColor: 'text-emerald-400', path: '/leads' },
  { label: 'Oportunidades', icon: Lightbulb, iconColor: 'text-amber-400', path: '/oportunidades', hideForLeadGeneratorOnly: true },
  { label: 'Os Meus Reconhecimentos', icon: Trophy, iconColor: 'text-emerald-400', path: '/recognitions/mine' },
];

const organizationSection = [
  { label: 'Utilizadores', icon: UserCog, iconColor: 'text-blue-400', path: '/users' },
  { label: 'Parceiros', icon: Handshake, iconColor: 'text-amber-400', path: '/partners' },
  { label: 'Prazos Chargeback', icon: Calendar, iconColor: 'text-orange-400', path: '/admin/chargeback-rules', superAdminOnly: true },
  { label: 'Prazos Fidelização', icon: Calendar, iconColor: 'text-indigo-400', path: '/admin/fidelizacao-rules', superAdminOnly: true },
  { label: 'Slug Violations', icon: Calendar, iconColor: 'text-rose-400', path: '/admin/slug-violations', superAdminOnly: true },
  { label: 'Campanhas', icon: ClipboardList, iconColor: 'text-cyan-400', path: '/admin/campanhas', allowedRoles: ['super_admin', 'gestor'] },
  { label: 'Tarifários (Simulador)', icon: Zap, iconColor: 'text-amber-300', path: '/admin/tarifarios', allowedRoles: ['super_admin', 'gestor'] },
  { label: 'Migrações de Dados', icon: Database, iconColor: 'text-rose-400', path: '/admin/data-migrations', superAdminOnly: true },
];

const activitiesSection = [
  { label: 'Emails', icon: Mail, iconColor: 'text-pink-400', path: '/activities?type=email' },
  { label: 'Reuniões', icon: Video, iconColor: 'text-violet-400', path: '/activities?type=reuniao' },
  { label: 'Chamadas', icon: Phone, iconColor: 'text-emerald-400', path: '/activities?type=chamada' },
  { label: 'Calendário', icon: Calendar, iconColor: 'text-sky-400', path: '/calendar' },
];

const CompanyItem = ({ company, category, subcategory, currentPath, collapsed }) => {
  const path = subcategory 
    ? `/contracts/${subcategory}/${company.key}`
    : `/contracts/${category}/${company.key}`;
  const isActive = currentPath === path;
  
  if (collapsed) return null;
  
  return (
    <Link
      to={path}
      data-testid={`sidebar-company-${company.key}`}
      className={`flex items-center gap-2 pl-9 pr-3 py-1 rounded-md text-xs transition-all duration-200 ${
        isActive
          ? 'bg-white/15 text-white'
          : 'text-slate-400 hover:bg-white/5 hover:text-slate-300'
      }`}
    >
      <span className="w-1 h-1 rounded-full bg-current" />
      {company.name}
    </Link>
  );
};

const SubcategorySection = ({ subcategory, subcategoryKey, category, currentPath, collapsed, isOpen, onToggle }) => {
  const Icon = subcategory.icon;
  
  if (collapsed) return null;
  
  return (
    <Collapsible open={isOpen} onOpenChange={() => onToggle(subcategoryKey)}>
      <CollapsibleTrigger className="flex items-center justify-between w-full pl-5 pr-3 py-1 ml-1 text-xs text-slate-400 hover:text-slate-200 transition-colors rounded-md hover:bg-white/5">
        <div className="flex items-center gap-2">
          <Icon className={`w-3 h-3 ${subcategory.iconColor}`} />
          <span>{subcategory.label}</span>
        </div>
        <ChevronRight className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
      </CollapsibleTrigger>
      <CollapsibleContent className="space-y-0.5 mt-1">
        {subcategory.companies.map((company) => (
          <CompanyItem
            key={company.key}
            company={company}
            category={category}
            subcategory={subcategoryKey}
            currentPath={currentPath}
            collapsed={collapsed}
          />
        ))}
      </CollapsibleContent>
    </Collapsible>
  );
};

const CategorySection = ({ categoryKey, category, currentPath, collapsed, isOpen, onToggle }) => {
  // Auto-detect which subcategory should be open from the URL on first render / path change
  const detectSub = useCallback(() => {
    if (!category.subcategories) return '';
    const found = Object.keys(category.subcategories).find(sub => currentPath.includes(`/${sub}/`));
    return found || '';
  }, [category.subcategories, currentPath]);
  const [openSub, setOpenSub] = useState(detectSub);
  useEffect(() => { setOpenSub(detectSub()); }, [detectSub]);
  const toggleSub = (key) => setOpenSub(prev => prev === key ? '' : key);

  const Icon = category.icon;
  
  if (collapsed) {
    // Get all company links for this category
    const allCompanies = [];
    if (category.subcategories) {
      Object.entries(category.subcategories).forEach(([subKey, sub]) => {
        sub.companies?.forEach(comp => {
          allCompanies.push({ ...comp, path: `/contracts/${subKey}/${comp.key}` });
        });
      });
    } else {
      category.companies?.forEach(comp => {
        allCompanies.push({ ...comp, path: `/contracts/${categoryKey}/${comp.key}` });
      });
    }
    
    return (
      <Popover>
        <PopoverTrigger asChild>
          <button className={`flex items-center justify-center w-10 h-10 rounded-lg mx-auto mb-1 transition-all hover:scale-105 ${category.bgColor}`}>
            <Icon className={`w-5 h-5 ${category.iconColor}`} />
          </button>
        </PopoverTrigger>
        <PopoverContent side="right" align="start" className="w-44 p-2">
          <p className="text-xs font-semibold text-slate-500 uppercase px-2 mb-1">{category.label}</p>
          {allCompanies.map(comp => (
            <Link
              key={comp.path}
              to={comp.path}
              className={`block px-2 py-1.5 text-sm rounded-md transition-colors ${
                currentPath === comp.path ? 'bg-[#2D3E6F] text-white' : 'text-slate-700 hover:bg-slate-100'
              }`}
            >
              {comp.name}
            </Link>
          ))}
        </PopoverContent>
      </Popover>
    );
  }
  
  return (
    <Collapsible open={isOpen} onOpenChange={() => onToggle(categoryKey)}>
      <CollapsibleTrigger 
        className="flex items-center justify-between w-full pl-5 pr-3 py-1.5 mb-0.5 text-slate-400 hover:text-slate-200 hover:bg-white/8 rounded-md transition-colors"
        data-testid={`sidebar-category-${categoryKey}`}
      >
        <div className="flex items-center gap-2.5">
          <Icon className={`w-3.5 h-3.5 flex-shrink-0 ${category.iconColor}`} />
          <span className="text-xs font-medium">{category.label}</span>
        </div>
        <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </CollapsibleTrigger>
      <CollapsibleContent className="space-y-0.5 mt-0.5 ml-2">
        {category.subcategories ? (
          Object.entries(category.subcategories).map(([subKey, subcategory]) => (
            <SubcategorySection
              key={subKey}
              subcategory={subcategory}
              subcategoryKey={subKey}
              category={categoryKey}
              currentPath={currentPath}
              collapsed={collapsed}
              isOpen={openSub === subKey}
              onToggle={toggleSub}
            />
          ))
        ) : (
          category.companies?.map((company) => (
            <CompanyItem
              key={company.key}
              company={company}
              category={categoryKey}
              subcategory={null}
              currentPath={currentPath}
              collapsed={collapsed}
            />
          ))
        )}
      </CollapsibleContent>
    </Collapsible>
  );
};

const SidebarSection = ({ title, items, currentPath, collapsed }) => {
  const pathMap = {
    'Tickets': '/tickets',
    'Base de Conhecimento': '/knowledge',
    'Utilizadores': '/users',
    'Parceiros': '/partners',
    'Emails': '/activities?type=email',
    'Reuniões': '/activities?type=reuniao',
    'Chamadas': '/activities?type=chamada',
    'Calendário': '/calendar',
  };
  
  if (collapsed && items.length > 0) {
    const firstItem = items[0];
    const FirstIcon = firstItem.icon;
    
    if (items.length === 1) {
      const path = firstItem.path || pathMap[firstItem.label] || '/';
      const isActive = currentPath === path || currentPath.startsWith(path.split('?')[0]);
      return (
        <div className="py-1 px-2">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Link
                  to={path}
                  className={`flex items-center justify-center w-10 h-10 rounded-lg mx-auto mb-1 transition-all duration-200 ${
                    isActive ? 'bg-white/15 text-white' : 'text-slate-400 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <FirstIcon className={`w-5 h-5 ${firstItem.iconColor}`} />
                </Link>
              </TooltipTrigger>
              <TooltipContent side="right"><p>{firstItem.label}</p></TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      );
    }
    
    // Multiple items: show popover
    return (
      <div className="py-1 px-2">
        <Popover>
          <PopoverTrigger asChild>
            <button className={`flex items-center justify-center w-10 h-10 rounded-lg mx-auto mb-1 text-slate-400 hover:bg-white/10 hover:text-white transition-all`}>
              <FirstIcon className={`w-5 h-5 ${firstItem.iconColor}`} />
            </button>
          </PopoverTrigger>
          <PopoverContent side="right" align="start" className="w-48 p-2">
            <p className="text-xs font-semibold text-slate-500 uppercase px-2 mb-1">{title}</p>
            {items.map((item) => {
              const Icon = item.icon;
              const path = item.path || pathMap[item.label] || '/';
              const isActive = currentPath === path;
              return (
                <Link
                  key={item.label}
                  to={path}
                  className={`flex items-center gap-2 px-2 py-1.5 text-sm rounded-md transition-colors ${
                    isActive ? 'bg-[#2D3E6F] text-white' : 'text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  <Icon className={`w-4 h-4 ${item.iconColor}`} />
                  {item.label}
                </Link>
              );
            })}
          </PopoverContent>
        </Popover>
      </div>
    );
  }
  
  return (
    <div className={`py-2 ${collapsed ? 'px-2' : 'px-3'}`}>
      {!collapsed && (
        <p className="px-3 mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
          {title}
        </p>
      )}
      {items.map((item) => {
        const Icon = item.icon;
        const path = item.path || pathMap[item.label] || '/';
        const isActive = currentPath === path || currentPath.startsWith(path.split('?')[0]);
        
        return (
          <Link
            key={item.label}
            to={path}
            data-testid={`sidebar-${item.label.toLowerCase().replace(/[\s.]/g, '-')}`}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg mb-1 transition-all duration-200 ${
              isActive
                ? 'bg-white/15 text-white border-l-[3px] border-[#4DD4E8]'
                : 'text-slate-300 hover:bg-white/10 hover:text-white'
            }`}
          >
            <Icon className={`w-5 h-5 flex-shrink-0 ${item.iconColor}`} />
            <span className="font-medium text-sm">{item.label}</span>
          </Link>
        );
      })}
    </div>
  );
};

// Wraps the CRM categories list with shared accordion state — only one category open at a time
const CRMCategoriesAccordion = ({ currentPath }) => {
  const detectCat = useCallback(() => {
    if (!currentPath.startsWith('/contracts/')) return '';
    for (const [catKey, cat] of Object.entries(CRM_STRUCTURE)) {
      if (currentPath.includes(`/${catKey}/`)) return catKey;
      if (cat.subcategories) {
        for (const subKey of Object.keys(cat.subcategories)) {
          if (currentPath.includes(`/${subKey}/`)) return catKey;
        }
      }
    }
    return '';
  }, [currentPath]);
  const [openCat, setOpenCat] = useState(detectCat);
  useEffect(() => { setOpenCat(detectCat()); }, [detectCat]);
  const toggleCat = (key) => setOpenCat(prev => prev === key ? '' : key);

  return (
    <>
      {Object.entries(CRM_STRUCTURE).map(([key, category]) => (
        <CategorySection
          key={key}
          categoryKey={key}
          category={category}
          currentPath={currentPath}
          collapsed={false}
          isOpen={openCat === key}
          onToggle={toggleCat}
        />
      ))}
    </>
  );
};

// Accordion navigation section
const AccordionNav = ({ title, sectionKey, icon: SectionIcon, iconColor, open, onToggle, collapsed, children, collapsedItems }) => {
  if (collapsed) {
    const items = collapsedItems || [];
    return (
      <div className="py-1 px-2">
        <Popover>
          <PopoverTrigger asChild>
            <button className="flex items-center justify-center w-10 h-10 rounded-lg mx-auto mb-1 text-slate-400 hover:bg-white/10 hover:text-white transition-all">
              <SectionIcon className={`w-5 h-5 ${iconColor}`} />
            </button>
          </PopoverTrigger>
          <PopoverContent side="right" align="start" className="w-48 p-2 max-h-[70vh] overflow-y-auto">
            <p className="text-xs font-semibold text-slate-500 uppercase px-2 mb-1">{title}</p>
            {items.map(item => (
              <Link key={item.path} to={item.path} className="flex items-center gap-2 px-2 py-1.5 text-sm rounded-md text-slate-700 hover:bg-slate-100 transition-colors">
                {item.icon && <item.icon className={`w-3.5 h-3.5 ${item.iconColor || ''}`} />}
                {item.label}
              </Link>
            ))}
            {items.length === 0 && React.Children.map(children, child => child)}
          </PopoverContent>
        </Popover>
      </div>
    );
  }

  return (
    <div className="px-3 py-1">
      <button
        onClick={() => onToggle(sectionKey)}
        className={`flex items-center justify-between w-full px-3 py-2 rounded-lg transition-all duration-200 group ${
          open ? 'text-white bg-white/8' : 'text-slate-400 hover:bg-white/5 hover:text-slate-200'
        }`}
        data-testid={`sidebar-section-${sectionKey}`}
      >
        <div className="flex items-center gap-2.5">
          <SectionIcon className={`w-4 h-4 ${iconColor}`} />
          <span className="text-xs font-semibold uppercase tracking-wider">{title}</span>
        </div>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>
      <div className={`overflow-hidden transition-all duration-200 ${open ? 'max-h-[500px] opacity-100 mt-1' : 'max-h-0 opacity-0'}`}>
        <div className="ml-5 pl-2 border-l border-white/10">
          {children}
        </div>
      </div>
    </div>
  );
};

// Single nav item - sub-item style (indented, smaller text)
const NavItem = ({ item, currentPath, badge }) => {
  const Icon = item.icon;
  const path = item.path || '/';
  // Exact match for most paths, startsWith only for paths with sub-routes
  const isActive = currentPath === path;
  return (
    <Link
      to={path}
      data-testid={`sidebar-${(item.label || '').toLowerCase().replace(/[\s.]/g, '-')}`}
      className={`flex items-center gap-2.5 pl-5 pr-3 py-1.5 rounded-md mb-0.5 transition-all duration-200 ${
        isActive
          ? 'bg-white/15 text-white'
          : 'text-slate-400 hover:bg-white/8 hover:text-slate-200'
      }`}
    >
      {Icon && <Icon className={`w-3.5 h-3.5 flex-shrink-0 ${item.iconColor || 'text-slate-500'}`} />}
      <span className="text-xs font-medium flex-1">{item.label}</span>
      {badge != null && badge > 0 && (
        <span
          className="text-[10px] font-bold bg-rose-500 text-white px-1.5 min-w-[18px] h-[18px] rounded-full flex items-center justify-center"
          data-testid={`sidebar-badge-${(item.label || '').toLowerCase()}`}
        >
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </Link>
  );
};

export const Layout = ({ children }) => {
  const { user, logout, hasPerm, canSeeSidebar, previewPartner, exitPreview } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [pushPrefsOpen, setPushPrefsOpen] = useState(false);
  const [chatUnread, setChatUnread] = useState(null);
  useEffect(() => {
    if (!user || ['angariador', 'parceiro'].includes(user.role)) return undefined;
    let prev = null;
    let rings = 0;
    const load = () => axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/chat/unread`).then((r) => {
      const n = r.data.unread || 0;
      if (prev !== null && n > prev) { playChatSound(); rings = 1; } else if (n > 0 && rings > 0 && rings < 3) { playChatSound(); rings += 1; }
      if (n === 0) rings = 0;
      prev = n;
      setChatUnread(n);
    }).catch(() => {});
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [user]);
  
  // Accordion state: which section is open
  // Helper: derive which section should be open for a given path
  const detectSection = (p) => {
    if (p === '/dashboard' || p === '/contracts/all') return '';
    if (p.startsWith('/contracts/')) return 'crm';
    if (p.startsWith('/dashboard/')) return 'producao';
    if (p.startsWith('/leads') || p.startsWith('/oportunidades') || p.startsWith('/recognitions')) return 'leads';
    if (p.startsWith('/financeiro') || p.startsWith('/importar')) return 'financeiro';
    if (p.startsWith('/tickets') || p.startsWith('/knowledge')) return 'suporte';
    if (p.startsWith('/activities') || p.startsWith('/calendar') || p.startsWith('/tools') || p.startsWith('/simulador') || p.startsWith('/avisos-telefone')) return 'ferramentas';
    if (p.startsWith('/users') || p.startsWith('/partners') || p.startsWith('/admin')) return 'organizacao';
    return '';
  };
  // Initialize synchronously from current path so sidebar layout is correct on first paint
  const [openSection, setOpenSection] = useState(() => detectSection(location.pathname));

  // Keep open section in sync when navigating
  useEffect(() => {
    setOpenSection(detectSection(location.pathname));
  }, [location.pathname]);

  // Persist sidebar scroll position across navigations (Layout remounts per page)
  const asideRef = useRef(null);
  useLayoutEffect(() => {
    const el = asideRef.current;
    if (!el) return;
    const saved = sessionStorage.getItem('crm-sidebar-scroll');
    if (saved) el.scrollTop = parseInt(saved, 10) || 0;
    const onScroll = () => sessionStorage.setItem('crm-sidebar-scroll', String(el.scrollTop));
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);
  
  const toggleSection = (key) => {
    setOpenSection(prev => prev === key ? '' : key);
  };
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notifCount, setNotifCount] = useState(0);
  const [lastNotifCount, setLastNotifCount] = useState(0);
  const [animateBell, setAnimateBell] = useState(false);
  const [sendAlertOpen, setSendAlertOpen] = useState(false);
  const [sendRecognitionOpen, setSendRecognitionOpen] = useState(false);
  const [canSendAlerts, setCanSendAlerts] = useState(false);
  const [canSendRecognition, setCanSendRecognition] = useState(false);
  const [ticketStats, setTicketStats] = useState({ pendentes: 0, unread: 0 });
  const [leadsReminder, setLeadsReminder] = useState({ total: 0, atrasadas: 0, hoje: 0, amanha: 0 });
  const [pendCount, setPendCount] = useState(0);
  const [reassignIds, setReassignIds] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

  // Pesquisa global (debounced)
  useEffect(() => {
    const term = searchQuery.trim();
    if (term.length < 2) {
      setSearchResults(null);
      setSearchOpen(false);
      return;
    }
    setSearchLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(`${API_URL}/search/global`, { params: { q: term } });
        setSearchResults(res.data);
        setSearchOpen(true);
      } catch (e) {
        setSearchResults(null);
      } finally {
        setSearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [searchQuery, API_URL]);

  const CONTRACT_CATEGORY_FALLBACK = 'energias';
  const goToSearchResult = (type, item) => {
    setSearchOpen(false);
    setSearchQuery('');
    if (type === 'lead') {
      navigate(`/leads/${item.id}`);
    } else if (type === 'contract') {
      navigate(`/contracts/${item.category || CONTRACT_CATEGORY_FALLBACK}/${item.company || 'edp'}/${item.id}`);
    } else if (type === 'client') {
      navigate(item.nif ? `/clientes/360?nif=${encodeURIComponent(item.nif)}` : `/clientes/360?name=${encodeURIComponent(item.name || '')}`);
    }
  };


  const fetchTicketStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/tickets/stats`);
      setTicketStats(res.data);
    } catch {
      // silent
    }
  }, [API_URL]);

  const fetchLeadsReminder = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/leads/reminder-count`);
      setLeadsReminder(res.data || { total: 0, atrasadas: 0, hoje: 0, amanha: 0 });
      const pc = await axios.get(`${API_URL}/pendencias/count`);
      setPendCount(pc.data?.abertas || 0);
    } catch {
      // silent
    }
  }, [API_URL]);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/notifications`);
      const data = res.data;
      const items = (data.notifications || []).map(n => ({
        id: n.id,
        type: n.type,
        title: n.title,
        text: n.message,
        link: n.link,
        meta: n.meta,
        time: n.created_at,
        read: n.read,
      }));
      setNotifications(items);
      const newCount = data.unread_count || 0;
      // Animate bell if new notifications arrived
      if (newCount > lastNotifCount && lastNotifCount >= 0) {
        setAnimateBell(true);
        setTimeout(() => setAnimateBell(false), 1500);
      }
      setLastNotifCount(newCount);
      setNotifCount(newCount);
    } catch (err) {
      // Fallback: if notifications endpoint fails, silently ignore
      console.error(err);
    }
  }, [API_URL, lastNotifCount]);

  // Poll for notifications every 30s
  useEffect(() => {
    fetchNotifications();
    fetchTicketStats();
    fetchLeadsReminder();
    const interval = setInterval(() => { fetchNotifications(); fetchTicketStats(); fetchLeadsReminder(); }, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications, fetchTicketStats, fetchLeadsReminder]);

  useEffect(() => {
    window.addEventListener('pendencias-changed', fetchLeadsReminder);
    return () => window.removeEventListener('pendencias-changed', fetchLeadsReminder);
  }, [fetchLeadsReminder]);

  // Also refresh on navigation
  useEffect(() => {
    fetchNotifications();
    fetchTicketStats();
    fetchLeadsReminder();
  }, [location.pathname]);

  // Fetch admin-alert send permission once after login
  useEffect(() => {
    if (!user) return;
    axios.get(`${API_URL}/admin-alerts/can-send`)
      .then(r => {
        setCanSendAlerts(!!(r.data?.alerts ?? r.data?.can_send));
        setCanSendRecognition(!!r.data?.recognition);
      })
      .catch(() => { setCanSendAlerts(false); setCanSendRecognition(false); });
  }, [user, API_URL]);

  const handleOpenNotif = () => {
    setNotifOpen(!notifOpen);
    if (!notifOpen) fetchNotifications();
  };

  const handleMarkAllRead = async () => {
    try {
      await axios.put(`${API_URL}/notifications/read-all`);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setNotifCount(0);
    } catch (err) {
      console.error(err);
    }
  };

  const handleNotifClick = async (notif) => {
    if (!notif.read) {
      try {
        await axios.put(`${API_URL}/notifications/${notif.id}/read`);
        setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, read: true } : n));
        setNotifCount(prev => Math.max(0, prev - 1));
      } catch (err) {
        console.error(err);
      }
    }
    if (notif.link) {
      navigate(notif.link);
      setNotifOpen(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const sidebarWidth = sidebarCollapsed ? 'w-16' : 'w-64';
  const mainMargin = sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64';

  return (
    <div className="min-h-screen bg-[#F3F4F6]">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        ref={asideRef}
        className={`fixed top-0 left-0 h-full ${sidebarWidth} bg-[#2D3E6F] z-50 transform transition-all duration-300 lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } overflow-y-auto`}
      >
        {/* Logo */}
        <div className={`p-4 border-b border-white/10 sticky top-0 bg-[#2D3E6F] z-10 ${sidebarCollapsed ? 'flex justify-center' : ''}`}>
          <Link to="/dashboard" className="flex items-center gap-3">
            {sidebarCollapsed ? (
              <img src={LOGO_URL} alt="Adiraqui" className="h-12 w-12 object-contain" />
            ) : (
              <img src={LOGO_URL} alt="Adiraqui" className="h-28" />
            )}
          </Link>
        </div>

        {/* Navigation */}
        <nav className="py-4">
          {/* Home - visible to all */}
          <div className={`py-2 ${sidebarCollapsed ? 'px-2' : 'px-3'}`}>
            {sidebarCollapsed ? (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Link
                      to={user?.role === 'angariador' ? '/leads' : '/dashboard'}
                      className={`flex items-center justify-center w-10 h-10 rounded-lg mx-auto mb-1 transition-all duration-200 ${
                        location.pathname === (user?.role === 'angariador' ? '/leads' : '/dashboard')
                          ? 'bg-white/15 text-white'
                          : 'text-slate-300 hover:bg-white/10 hover:text-white'
                      }`}
                    >
                      <LayoutDashboard className="w-5 h-5 text-[#4DD4E8]" />
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent side="right">
                    <p>{user?.role === 'angariador' ? 'Leads' : 'Dashboard'}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : (
              <Link
                to={user?.role === 'angariador' ? '/leads' : '/dashboard'}
                data-testid="sidebar-home"
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg mb-1 transition-all duration-200 ${
                  location.pathname === (user?.role === 'angariador' ? '/leads' : '/dashboard')
                    ? 'bg-white/15 text-white border-l-[3px] border-[#4DD4E8]'
                    : 'text-slate-300 hover:bg-white/10 hover:text-white'
                }`}
              >
                <LayoutDashboard className="w-5 h-5 flex-shrink-0 text-[#4DD4E8]" />
                <span className="font-medium text-sm">{user?.role === 'angariador' ? 'As Minhas Leads' : 'Dashboard'}</span>
              </Link>
            )}
          </div>

          {/* Angariador sees only Leads section */}
          {user?.role === 'angariador' ? (
            // Angariador: vista simplificada — só "As Minhas Leads" (acima) + Performance.
            // NÃO renderizar o accordion LEADS completo (Pipeline/Importar/Follow-ups/Oportunidades
            // não fazem sentido para o role angariador). Performance é útil porque ele já tem
            // audience='angariadores' no /leads/stats/conversion.
            <div className={`${sidebarCollapsed ? 'px-2' : 'px-3'}`}>
              {sidebarCollapsed ? (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Link to="/leads/stats" className={`flex items-center justify-center w-10 h-10 rounded-lg mx-auto mb-1 transition-all ${location.pathname === '/leads/stats' ? 'bg-white/15 text-white' : 'text-slate-300 hover:bg-white/10 hover:text-white'}`}>
                        <TrendingUp className="w-5 h-5 text-amber-300" />
                      </Link>
                    </TooltipTrigger>
                    <TooltipContent side="right"><p>Performance</p></TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : (
                <Link
                  to="/leads/stats"
                  data-testid="sidebar-angariador-performance"
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg mb-1 transition-all duration-200 ${
                    location.pathname === '/leads/stats'
                      ? 'bg-white/15 text-white border-l-[3px] border-amber-300'
                      : 'text-slate-300 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <TrendingUp className="w-5 h-5 flex-shrink-0 text-amber-300" />
                  <span className="font-medium text-sm">Performance</span>
                </Link>
              )}
            </div>
          ) : (
            <>
              {/* Contratos Geral - Fixed item below Home */}
              {hasPerm('ver_contratos') && canSeeSidebar('contratos_geral') && (
                <div className={`${sidebarCollapsed ? 'px-2' : 'px-3'}`}>
                  {sidebarCollapsed ? (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Link to="/contracts/all" className={`flex items-center justify-center w-10 h-10 rounded-lg mx-auto mb-1 transition-all ${location.pathname === '/contracts/all' ? 'bg-white/15 text-white' : 'text-slate-300 hover:bg-white/10 hover:text-white'}`}>
                            <FileText className="w-5 h-5 text-sky-300" />
                          </Link>
                        </TooltipTrigger>
                        <TooltipContent side="right"><p>Contratos Geral</p></TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ) : (
                    <Link
                      to="/contracts/all"
                      data-testid="sidebar-contratos-geral"
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg mb-1 transition-all duration-200 ${
                        location.pathname === '/contracts/all'
                          ? 'bg-white/15 text-white border-l-[3px] border-sky-300'
                          : 'text-slate-300 hover:bg-white/10 hover:text-white'
                      }`}
                    >
                      <FileText className="w-5 h-5 flex-shrink-0 text-sky-300" />
                      <span className="font-medium text-sm">Contratos Geral</span>
                    </Link>
                  )}
                </div>
              )}

              {/* CRM - Categories only */}
              {hasPerm('ver_contratos') && canSeeSidebar('crm') && (
                <AccordionNav title="CRM" sectionKey="crm" icon={ClipboardList} iconColor="text-[#4DD4E8]" open={openSection === 'crm'} onToggle={toggleSection} collapsed={sidebarCollapsed}
                  collapsedItems={
                    Object.entries(CRM_STRUCTURE).flatMap(([catKey, cat]) => {
                      if (cat.subcategories) {
                        return Object.entries(cat.subcategories).flatMap(([subKey, sub]) =>
                          (sub.companies || []).map(c => ({ label: c.name, path: `/contracts/${subKey}/${c.key}`, icon: cat.icon, iconColor: cat.iconColor }))
                        );
                      }
                      return (cat.companies || []).map(c => ({ label: c.name, path: `/contracts/${catKey}/${c.key}`, icon: cat.icon, iconColor: cat.iconColor }));
                    })
                  }
                >
                  <CRMCategoriesAccordion currentPath={location.pathname} />
                </AccordionNav>
              )}

              {['super_admin', 'gestor'].includes(user?.role) && (
                <NavItem
                  item={{ label: 'Centro de Comando', icon: Gauge, iconColor: 'text-amber-300', path: '/centro-comando' }}
                  currentPath={location.pathname}
                />
              )}
              {!['angariador', 'parceiro'].includes(user?.role) && (
                <NavItem
                  item={{ label: 'Chat', icon: MessageSquare, iconColor: 'text-sky-300', path: '/chat' }}
                  currentPath={location.pathname}
                  badge={chatUnread || undefined}
                />
              )}
              {['super_admin', 'gestor', 'chefe_equipa'].includes(user?.role) && (
                <NavItem
                  item={{ label: 'Metas', icon: Target, iconColor: 'text-emerald-300', path: '/metas' }}
                  currentPath={location.pathname}
                />
              )}
              {user?.role !== 'angariador' && (
                <NavItem
                  item={{ label: 'Pendentes por resolver', icon: BellRing, iconColor: 'text-rose-400', path: '/pendencias' }}
                  currentPath={location.pathname}
                  badge={pendCount || undefined}
                />
              )}

              {/* Produção */}
              {(hasPerm('ver_prod_parceiros') || hasPerm('ver_prod_comerciais') || hasPerm('ver_prod_angariadores')) && canSeeSidebar('producao') && (
                <AccordionNav title="Producao" sectionKey="producao" icon={BarChart3} iconColor="text-orange-400" open={openSection === 'producao'} onToggle={toggleSection} collapsed={sidebarCollapsed}>
                  {(() => {
                    let items = productionSection;
                    if (['admin', 'parceiro'].includes(user?.role)) {
                      items = productionSection.filter(item => {
                        if (item.path === '/dashboard/parceiros') return hasPerm('ver_prod_parceiros');
                        if (item.path === '/dashboard/comerciais') return hasPerm('ver_prod_comerciais');
                        if (item.path === '/dashboard/angariadores') return hasPerm('ver_prod_angariadores');
                        return true;
                      });
                    } else if (user?.role === 'chefe_equipa') {
                      items = [{ label: 'Prod. Equipa', icon: Users, iconColor: 'text-sky-400', path: '/dashboard/comerciais' }];
                    } else if (user?.role === 'comercial') {
                      items = [{ label: 'A Minha Producao', icon: Users, iconColor: 'text-sky-400', path: '/dashboard/comerciais' }];
                    }
                    return items.map(item => <NavItem key={item.label} item={item} currentPath={location.pathname} />);
                  })()}
                </AccordionNav>
              )}

              {/* Conversoes — read-only simplified list for restricted partners (e.g. Credível) */}
              {canSeeSidebar('conversoes', false) && (
                <NavItem
                  item={{ label: 'Conversões', icon: TrendingUp, iconColor: 'text-emerald-300', path: '/parceiro/conversoes' }}
                  currentPath={location.pathname}
                />
              )}

              {/* Leads */}
              {hasPerm('ver_leads') && canSeeSidebar('leads') && (
                <AccordionNav title="Leads" sectionKey="leads" icon={Link2} iconColor="text-teal-400" open={openSection === 'leads'} onToggle={toggleSection} collapsed={sidebarCollapsed}>
                  {leadsSection
                    .filter(item => !item.allowedRoles || item.allowedRoles.includes(user?.role))
                    .filter(item => {
                      // Hide items meant only for partners with comerciais (not lead-generator-only partners like Credível)
                      if (item.hideForLeadGeneratorOnly) {
                        const lwp = user?.partner_permissions?.lead_workers_partner_ids;
                        const isLeadGeneratorOnly = ['admin', 'parceiro'].includes(user?.role) && Array.isArray(lwp) && lwp.length > 0;
                        if (isLeadGeneratorOnly) return false;
                      }
                      return true;
                    })
                    .filter(item => {
                      // Sub-permission check by sidebar key
                      const subKeyMap = {
                        '/leads/pipeline': 'leads_pipeline',
                        '/leads/importar': 'leads_importar',
                        '/leads/stats': 'leads_performance',
                        '/leads/follow-ups-fatura': 'leads_followups',
                        '/leads': 'leads_externas',
                        '/oportunidades': 'oportunidades',
                      };
                      const subKey = subKeyMap[item.path];
                      if (!subKey) return true;
                      if (item.path === '/oportunidades' && ['comercial', 'chefe_equipa'].includes(user?.role) && !previewPartner) return true;
                      const baseDefault = item.path === '/oportunidades' ? false : true;
                      if (!canSeeSidebar(subKey, baseDefault)) return false;
                      if (item.path === '/oportunidades') return hasPerm('ver_oportunidades');
                      return true;
                    })
                    .map(item => {
                      const badge = item.path === '/leads/pipeline' ? leadsReminder.total : undefined;
                      return <NavItem key={item.label} item={item} currentPath={location.pathname} badge={badge} />;
                    })}
                </AccordionNav>
              )}

              {/* Financeiro - NOT visible to backoffice (secretarial role) */}
              {user?.role !== 'backoffice' && canSeeSidebar('financeiro') && (hasPerm('ver_financeiro') || ['admin', 'parceiro', 'comercial', 'chefe_equipa'].includes(user?.role)) && (
                <AccordionNav title="Financeiro" sectionKey="financeiro" icon={Wallet} iconColor="text-emerald-400" open={openSection === 'financeiro'} onToggle={toggleSection} collapsed={sidebarCollapsed}>
                  {['super_admin', 'gestor'].includes(user?.role) ? (
                    <>
                      {canSeeSidebar('financeiro_gestao') && (
                        <NavItem item={{ label: 'Gestao Financeira', icon: Wallet, iconColor: 'text-emerald-400', path: '/financeiro' }} currentPath={location.pathname} />
                      )}
                      {canSeeSidebar('financeiro_importar') && (
                        <NavItem item={{ label: 'Importar Contratos', icon: Upload, iconColor: 'text-blue-400', path: '/importar' }} currentPath={location.pathname} />
                      )}
                    </>
                  ) : (
                    canSeeSidebar('financeiro_autos') && (
                      <NavItem item={{ label: 'Autos', icon: Wallet, iconColor: 'text-emerald-400', path: '/financeiro' }} currentPath={location.pathname} />
                    )
                  )}
                </AccordionNav>
              )}

              {/* Suporte */}
              {canSeeSidebar('suporte') && (
                <AccordionNav title="Suporte" sectionKey="suporte" icon={Ticket} iconColor="text-rose-400" open={openSection === 'suporte'} onToggle={toggleSection} collapsed={sidebarCollapsed}>
                  {supportSection
                    .filter(item => {
                      if (item.path === '/tickets') return canSeeSidebar('suporte_tickets');
                      if (item.path === '/knowledge') return canSeeSidebar('suporte_kb');
                      return true;
                    })
                    .map(item => {
                      const badge = item.path === '/tickets' ? ticketStats.pendentes : undefined;
                      return <NavItem key={item.label} item={item} currentPath={location.pathname} badge={badge} />;
                    })}
                </AccordionNav>
              )}

              {/* Ferramentas (Emails / Reunioes / Chamadas / Calendario / Simulador) - hidden for angariadores */}
              {user?.role !== 'angariador' && canSeeSidebar('ferramentas') && (
                (() => {
                  const itemKey = (path) => {
                    if (path.includes('type=email')) return 'emails';
                    if (path.includes('type=reuniao')) return 'reunioes';
                    if (path.includes('type=chamada')) return 'chamadas';
                    if (path === '/calendar') return 'calendario';
                    return '';
                  };
                  const visibleItems = activitiesSection.filter(item => canSeeSidebar(`ferramentas_${itemKey(item.path)}`));
                  const showIberdrola = canSeeSidebar('ferramentas_iberdrola');
                  const showSimulador = canSeeSidebar('ferramentas_simulador');
                  // 2026-05-21: Simulador é independente dos outros toggles — mostra sempre se não for angariador
                  if (visibleItems.length === 0 && !showIberdrola && !showSimulador) return null;
                  return (
                    <AccordionNav title="Ferramentas" sectionKey="ferramentas" icon={Wrench} iconColor="text-amber-300" open={openSection === 'ferramentas'} onToggle={toggleSection} collapsed={sidebarCollapsed}>
                      {visibleItems.map(item => <NavItem key={item.label} item={item} currentPath={location.pathname} />)}
                      {showIberdrola && (
                        <NavItem
                          item={{ label: 'Verificar Iberdrola', icon: ShieldCheck, iconColor: 'text-lime-300', path: '/tools/verify-iberdrola' }}
                          currentPath={location.pathname}
                        />
                      )}
                      {showSimulador && (
                        <NavItem
                          item={{ label: 'Simulador Energia', icon: Calculator, iconColor: 'text-indigo-300', path: '/simulador' }}
                          currentPath={location.pathname}
                        />
                      )}
                      {['super_admin', 'gestor', 'admin'].includes(user?.role) && (
                        <NavItem
                          item={{ label: 'Avisos no telefone', icon: BellRing, iconColor: 'text-rose-300', path: '/avisos-telefone' }}
                          currentPath={location.pathname}
                        />
                      )}
                    </AccordionNav>
                  );
                })()
              )}

              {/* Organização */}
              {hasPerm('gerir_membros') && canSeeSidebar('organizacao') && ['super_admin', 'gestor', 'admin', 'parceiro', 'backoffice'].includes(user?.role) && (
                <AccordionNav title="Organizacao" sectionKey="organizacao" icon={UsersRound} iconColor="text-indigo-400" open={openSection === 'organizacao'} onToggle={toggleSection} collapsed={sidebarCollapsed}>
                  {organizationSection
                    .filter(item => !item.superAdminOnly || user?.role === 'super_admin')
                    .filter(item => !item.allowedRoles || item.allowedRoles.includes(user?.role))
                    .map(item => <NavItem key={item.label} item={item} currentPath={location.pathname} />)}
                </AccordionNav>
              )}
            </>
          )}

          {/* Centro de Ajuda — visível a todos os perfis, no final */}
          <div className={`${sidebarCollapsed ? 'px-2' : 'px-3'} mt-2 pt-2 border-t border-white/10`}>
            {sidebarCollapsed ? (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Link to="/centro-ajuda" data-testid="sidebar-help" className={`flex items-center justify-center w-10 h-10 rounded-lg mx-auto mb-1 transition-all ${location.pathname === '/centro-ajuda' ? 'bg-white/15 text-white' : 'text-slate-300 hover:bg-white/10 hover:text-white'}`}>
                      <HelpCircle className="w-5 h-5 text-emerald-300" />
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent side="right"><p>Centro de Ajuda</p></TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : (
              <Link
                to="/centro-ajuda"
                data-testid="sidebar-help"
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg mb-1 transition-all duration-200 ${
                  location.pathname === '/centro-ajuda'
                    ? 'bg-white/15 text-white border-l-[3px] border-emerald-300'
                    : 'text-slate-300 hover:bg-white/10 hover:text-white'
                }`}
              >
                <HelpCircle className="w-5 h-5 flex-shrink-0 text-emerald-300" />
                <span className="font-medium text-sm">Centro de Ajuda</span>
              </Link>
            )}
          </div>
        </nav>

        {/* Collapse button & User section */}
        <div className="sticky bottom-0 bg-[#2D3E6F] border-t border-white/10">
          {/* Collapse toggle */}
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className={`w-full py-3 flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/5 transition-colors border-b border-white/10`}
            data-testid="collapse-sidebar-button"
          >
            {sidebarCollapsed ? (
              <ChevronRight className="w-5 h-5" />
            ) : (
              <ChevronLeft className="w-5 h-5" />
            )}
          </button>
          
          {/* User section */}
          <div className={`p-4 ${sidebarCollapsed ? 'flex justify-center' : ''}`}>
            {sidebarCollapsed ? (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button onClick={handleLogout}>
                      <Avatar className="h-10 w-10 border-2 border-white/20">
                        <AvatarImage src={avatarSrc(user?.avatar_url)} className="object-cover" />
                        <AvatarFallback className="bg-[#4DD4E8] text-white font-semibold">
                          {getInitials(user?.name)}
                        </AvatarFallback>
                      </Avatar>
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="right">
                    <p>{user?.name} - Sair</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : (
              <div className="flex items-center gap-3">
                <Avatar className="h-10 w-10 border-2 border-white/20 cursor-pointer" onClick={() => navigate('/profile')}>
                  <AvatarImage src={avatarSrc(user?.avatar_url)} className="object-cover" />
                  <AvatarFallback className="bg-[#4DD4E8] text-white font-semibold">
                    {getInitials(user?.name)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0 cursor-pointer" onClick={() => navigate('/profile')}>
                  <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                  <p className="text-xs text-slate-400 capitalize">{{ super_admin: 'Super Admin', gestor: 'Gestor', admin: 'Admin', parceiro: 'Parceiro', backoffice: 'Backoffice', comercial: 'Comercial', chefe_equipa: 'Chefe de Equipa', angariador: 'Angariador' }[user?.role] || user?.role}</p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setPushPrefsOpen(true)}
                  className="text-slate-400 hover:text-white hover:bg-white/10"
                  title="Avisos no telefone"
                  data-testid="sidebar-push-prefs-button"
                >
                  <BellRing className="w-5 h-5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleLogout}
                  className="text-slate-400 hover:text-white hover:bg-white/10"
                  data-testid="logout-button"
                >
                  <LogOut className="w-5 h-5" />
                </Button>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className={`${mainMargin} transition-all duration-300 min-w-0 overflow-x-hidden`}>
        {/* Header */}
        <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b border-slate-200/50">
          <div className="flex items-center justify-between px-4 lg:px-6 py-3">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden"
                onClick={() => setSidebarOpen(true)}
                data-testid="mobile-menu-button"
              >
                <Menu className="w-6 h-6" />
              </Button>

              <div className="relative hidden md:block">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Procurar leads, contratos, clientes..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => { if (searchResults) setSearchOpen(true); }}
                  onKeyDown={(e) => { if (e.key === 'Escape') { setSearchOpen(false); e.target.blur(); } }}
                  className="pl-10 w-80 bg-slate-50 border-slate-200 focus:bg-white"
                  data-testid="search-input"
                />
                {searchOpen && (
                  <>
                    <div className="fixed inset-0 z-30" onClick={() => setSearchOpen(false)} />
                    <div className="absolute left-0 top-full mt-2 w-[480px] max-h-[70vh] overflow-y-auto bg-white rounded-xl shadow-2xl border border-slate-100 z-40 p-2" data-testid="global-search-results">
                      {searchLoading && <div className="px-3 py-4 text-sm text-slate-400">A procurar...</div>}
                      {!searchLoading && searchResults && (() => {
                        const { leads = [], contracts = [], clients = [] } = searchResults;
                        const empty = leads.length === 0 && contracts.length === 0 && clients.length === 0;
                        if (empty) return <div className="px-3 py-4 text-sm text-slate-400">Sem resultados para "{searchQuery}"</div>;
                        return (
                          <div className="space-y-3">
                            {leads.length > 0 && (
                              <div>
                                <p className="px-2 py-1 text-[11px] font-bold uppercase tracking-wide text-slate-400">Leads</p>
                                {leads.map((l) => (
                                  <button key={l.id} type="button" onClick={() => goToSearchResult('lead', l)} className="w-full text-left flex items-center justify-between gap-2 px-3 py-2 rounded-lg hover:bg-slate-50" data-testid={`search-lead-${l.id}`}>
                                    <span className="min-w-0">
                                      <span className="block text-sm font-medium text-slate-800 truncate">{l.name}</span>
                                      <span className="block text-xs text-slate-400 truncate">{l.phone || l.nif || '—'}</span>
                                    </span>
                                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${l.status === 'convertida' ? 'bg-emerald-100 text-emerald-700' : l.status === 'em_contacto' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>{l.status}</span>
                                  </button>
                                ))}
                              </div>
                            )}
                            {contracts.length > 0 && (
                              <div>
                                <p className="px-2 py-1 text-[11px] font-bold uppercase tracking-wide text-slate-400">Contratos</p>
                                {contracts.map((c) => (
                                  <button key={c.id} type="button" onClick={() => goToSearchResult('contract', c)} className="w-full text-left flex items-center justify-between gap-2 px-3 py-2 rounded-lg hover:bg-slate-50" data-testid={`search-contract-${c.id}`}>
                                    <span className="min-w-0">
                                      <span className="block text-sm font-medium text-slate-800 truncate">{c.name}</span>
                                      <span className="block text-xs text-slate-400 truncate">#{c.codigo} · {c.nif || '—'}</span>
                                    </span>
                                    {c.company && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 capitalize">{c.company}</span>}
                                  </button>
                                ))}
                              </div>
                            )}
                            {clients.length > 0 && (
                              <div>
                                <p className="px-2 py-1 text-[11px] font-bold uppercase tracking-wide text-slate-400">Clientes</p>
                                {clients.map((c, i) => (
                                  <button key={c.nif || c.name || i} type="button" onClick={() => goToSearchResult('client', c)} className="w-full text-left flex items-center justify-between gap-2 px-3 py-2 rounded-lg hover:bg-slate-50" data-testid={`search-client-${i}`}>
                                    <span className="min-w-0">
                                      <span className="block text-sm font-medium text-slate-800 truncate">{c.name}</span>
                                      <span className="block text-xs text-slate-400 truncate">{c.nif || '—'} · {c.count} contrato(s){c.companies?.length ? ` · ${c.companies.join(', ')}` : ''}</span>
                                    </span>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="flex items-center gap-3">
              {/* Global refresh — emits CustomEvent('crm:refresh') for the current page to listen to */}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  const evt = new CustomEvent('crm:refresh');
                  window.dispatchEvent(evt);
                  toast.success('Página actualizada', { duration: 1500 });
                }}
                className="text-slate-600 hover:text-slate-900"
                title="Actualizar página"
                data-testid="global-refresh-btn"
              >
                <RefreshCw className="w-5 h-5" />
              </Button>
              {/* Send Alert (super_admin / gestor / users with can_send_alerts) */}
              {canSendAlerts && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setSendAlertOpen(true)}
                  className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                  title="Enviar alerta forte a um utilizador"
                  data-testid="send-alert-btn"
                >
                  <AlertTriangle className="w-5 h-5" />
                </Button>
              )}
              {/* Send Recognition (super_admin / gestor / users with can_send_recognition) */}
              {canSendRecognition && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setSendRecognitionOpen(true)}
                  className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                  title="Enviar reconhecimento a um utilizador"
                  data-testid="send-recognition-btn"
                >
                  <Trophy className="w-5 h-5" />
                </Button>
              )}
              {/* Notifications */}
              <div className="relative">
                <Button
                  variant="ghost"
                  size="icon"
                  className={`relative text-slate-600 hover:text-slate-900 ${animateBell ? 'animate-bounce' : ''}`}
                  data-testid="notifications-button"
                  onClick={handleOpenNotif}
                >
                  <Bell className="w-5 h-5" />
                  {notifCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-[#E94C88] rounded-full text-[10px] text-white font-bold flex items-center justify-center px-1 animate-pulse" data-testid="notif-badge">
                      {notifCount > 99 ? '99+' : notifCount}
                    </span>
                  )}
                </Button>

                {notifOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setNotifOpen(false)} />
                    <div className="absolute right-0 top-12 z-50 w-96 bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden" data-testid="notifications-panel">
                      <div className="flex items-center justify-between p-4 border-b border-slate-100">
                        <h3 className="text-sm font-semibold text-slate-800">Notificacoes</h3>
                        <div className="flex items-center gap-2">
                          {notifCount > 0 && (
                            <button 
                              onClick={handleMarkAllRead} 
                              className="text-xs text-[#2D3E6F] hover:underline"
                              data-testid="mark-all-read-btn"
                            >
                              Marcar todas como lidas
                            </button>
                          )}
                          <button onClick={() => setNotifOpen(false)} className="text-slate-400 hover:text-slate-600">
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      <div className="max-h-[400px] overflow-y-auto divide-y divide-slate-50">
                        {notifications.length === 0 ? (
                          <div className="p-6 text-center text-sm text-slate-400">Sem notificacoes</div>
                        ) : (
                          notifications.map((notif) => (
                            <div
                              key={notif.id}
                              className={`flex items-start gap-3 p-3.5 hover:bg-slate-50 transition-colors cursor-pointer ${!notif.read ? 'bg-blue-50/40 border-l-[3px] border-l-[#2D3E6F]' : ''}`}
                              onClick={() => handleNotifClick(notif)}
                              data-testid={`notification-item-${notif.id}`}
                            >
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                                notif.type === 'new_lead' ? 'bg-emerald-100' :
                                notif.type === 'lead_status' ? 'bg-amber-100' :
                                notif.type === 'contract_created' ? 'bg-blue-100' :
                                notif.type === 'contract_status' ? 'bg-purple-100' :
                                notif.type === 'contract_assigned' ? 'bg-indigo-100' :
                                notif.type === 'ticket_created' ? 'bg-cyan-100' :
                                notif.type === 'ticket_updated' ? 'bg-teal-100' :
                                notif.type === 'team_member' ? 'bg-orange-100' :
                                notif.type === 'auto_lancado' ? 'bg-emerald-100' :
                                'bg-slate-100'
                              }`}>
                                {notif.type === 'new_lead' && <UserPlus className="w-4 h-4 text-emerald-600" />}
                                {notif.type === 'lead_status' && <FileText className="w-4 h-4 text-amber-600" />}
                                {notif.type === 'contract_created' && <ClipboardList className="w-4 h-4 text-blue-600" />}
                                {notif.type === 'contract_status' && <FileCheck className="w-4 h-4 text-purple-600" />}
                                {notif.type === 'contract_assigned' && <ArrowRightLeft className="w-4 h-4 text-indigo-600" />}
                                {notif.type === 'ticket_created' && <Ticket className="w-4 h-4 text-cyan-600" />}
                                {notif.type === 'ticket_updated' && <Ticket className="w-4 h-4 text-teal-600" />}
                                {notif.type === 'team_member' && <Users className="w-4 h-4 text-orange-600" />}
                                {notif.type === 'auto_lancado' && <Wallet className="w-4 h-4 text-emerald-600" />}
                                {!['new_lead','lead_status','contract_created','contract_status','contract_assigned','ticket_created','ticket_updated','team_member','auto_lancado'].includes(notif.type) && <Bell className="w-4 h-4 text-slate-500" />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className={`text-sm ${!notif.read ? 'font-semibold' : ''} text-slate-700 line-clamp-1`}>
                                  {notif.title}
                                </p>
                                <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{notif.text}</p>
                                {notif.type === 'lead_nao_vista' && notif.meta?.lead_ids?.length > 0 && ['super_admin', 'gestor', 'admin'].includes(user?.role) && (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); setReassignIds(notif.meta.lead_ids); }}
                                    className="mt-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full bg-[#2D3E6F] text-white hover:bg-[#4DD4E8] hover:text-[#2D3E6F] transition-colors"
                                    data-testid={`notif-reassign-${notif.id}`}
                                  >
                                    Passar a outro comercial
                                  </button>
                                )}
                                {notif.time && (
                                  <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                                    <Clock className="w-3 h-3" />
                                    {(() => { try { const d = new Date(notif.time); const now = new Date(); const diff = (now - d) / 60000; if (diff < 1) return 'Agora'; if (diff < 60) return `${Math.floor(diff)}m`; if (diff < 1440) return `${Math.floor(diff/60)}h`; return `${Math.floor(diff/1440)}d`; } catch { return ''; } })()}
                                  </p>
                                )}
                              </div>
                              {!notif.read && (
                                <div className="w-2 h-2 rounded-full bg-[#2D3E6F] mt-2 flex-shrink-0" />
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>

              {hasPerm('criar_contratos') && user?.role !== 'angariador' && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    className="bg-[#2D3E6F] hover:bg-[#1a2a4a] text-white rounded-full gap-2"
                    data-testid="create-button"
                  >
                    <Plus className="w-4 h-4" />
                    Criar
                    <ChevronDown className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 max-h-[70vh] overflow-y-auto">
                  <DropdownMenuLabel>Luz e Gás</DropdownMenuLabel>
                  <DropdownMenuItem onClick={() => navigate('/contracts/luz_gas/edp/new')}>
                    <Zap className="w-4 h-4 mr-2 text-yellow-500" /> Criar EDP
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/contracts/luz_gas/galp/new')}>
                    <Zap className="w-4 h-4 mr-2 text-yellow-500" /> Criar Galp
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/contracts/luz_gas/endesa/new')}>
                    <Zap className="w-4 h-4 mr-2 text-yellow-500" /> Criar Endesa
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/contracts/luz_gas/iberdrola/new')}>
                    <Zap className="w-4 h-4 mr-2 text-yellow-500" /> Criar Iberdrola
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/contracts/luz_gas/gold/new')}>
                    <Zap className="w-4 h-4 mr-2 text-yellow-500" /> Criar Gold Energy
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/contracts/luz_gas/repsol/new')}>
                    <Zap className="w-4 h-4 mr-2 text-yellow-500" /> Criar Repsol
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/contracts/luz_gas/yes/new')}>
                    <Zap className="w-4 h-4 mr-2 text-yellow-500" /> Criar Yes
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/contracts/luz_gas/meo_energia/new')} data-testid="create-meo-energia-btn">
                    <Zap className="w-4 h-4 mr-2 text-yellow-500" /> Criar Meo Energia
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel>Solar</DropdownMenuLabel>
                  <DropdownMenuItem onClick={() => navigate('/contracts/solar/edp/new')}>
                    <Sun className="w-4 h-4 mr-2 text-amber-500" /> Criar EDP Solar
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/contracts/solar/galp/new')}>
                    <Sun className="w-4 h-4 mr-2 text-amber-500" /> Criar Galp Solar
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/contracts/solar/iberdrola/new')}>
                    <Sun className="w-4 h-4 mr-2 text-amber-500" /> Criar Iberdrola Solar
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel>Carregamento Elétrico</DropdownMenuLabel>
                  <DropdownMenuItem onClick={() => navigate('/contracts/carregamento/edp/new')}>
                    <BatteryCharging className="w-4 h-4 mr-2 text-green-500" /> Criar EDP Carreg.
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/contracts/carregamento/galp/new')}>
                    <BatteryCharging className="w-4 h-4 mr-2 text-green-500" /> Criar Galp Carreg.
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/contracts/carregamento/iberdrola/new')}>
                    <BatteryCharging className="w-4 h-4 mr-2 text-green-500" /> Criar Iberdrola Carreg.
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel>Telecomunicações</DropdownMenuLabel>
                  <DropdownMenuItem onClick={() => navigate('/contracts/telecomunicacoes/nos/new')}>
                    <Tv className="w-4 h-4 mr-2 text-purple-500" /> Criar NOS
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/contracts/telecomunicacoes/meo/new')}>
                    <Tv className="w-4 h-4 mr-2 text-purple-500" /> Criar MEO
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/contracts/telecomunicacoes/vodafone/new')}>
                    <Tv className="w-4 h-4 mr-2 text-purple-500" /> Criar Vodafone
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel>Alarmes</DropdownMenuLabel>
                  <DropdownMenuItem onClick={() => navigate('/contracts/alarmes/nos_securitas/new')}>
                    <Shield className="w-4 h-4 mr-2 text-red-500" /> Criar NOS Securitas
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/contracts/alarmes/prosegur/new')}>
                    <Shield className="w-4 h-4 mr-2 text-red-500" /> Criar Prosegur
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/tickets/new')}>
                    <Ticket className="w-4 h-4 mr-2 text-cyan-500" /> Novo Ticket
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              )}

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="gap-2 hidden md:flex" data-testid="user-menu-button">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={avatarSrc(user?.avatar_url)} className="object-cover" />
                      <AvatarFallback className="bg-[#4DD4E8] text-white text-sm">
                        {getInitials(user?.name)}
                      </AvatarFallback>
                    </Avatar>
                    <span className="font-medium text-slate-700">{user?.name?.split(' ')[0]}</span>
                    <ChevronDown className="w-4 h-4 text-slate-400" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>Minha Conta</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/settings')}>
                    <Settings className="w-4 h-4 mr-2" />
                    Definições
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setPushPrefsOpen(true)} data-testid="header-push-prefs">
                    <BellRing className="w-4 h-4 mr-2" />
                    Avisos no telefone
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                    <LogOut className="w-4 h-4 mr-2" />
                    Terminar Sessão
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Preview-as-partner banner (super_admin/gestor only) */}
        {previewPartner && (
          <div className="sticky top-0 z-40 bg-amber-500 text-white px-4 py-2 flex items-center justify-between gap-3 shadow-md" data-testid="preview-banner">
            <div className="flex items-center gap-2 text-sm">
              <span className="text-base">🔍</span>
              <span>
                <strong>Pré-visualização:</strong> a ver o sistema como <strong>{previewPartner.name}</strong> (sidebar e botões filtrados).
              </span>
            </div>
            <button
              onClick={exitPreview}
              className="text-xs font-bold uppercase tracking-wide bg-amber-700/40 hover:bg-amber-700/60 px-3 py-1 rounded-full transition"
              data-testid="exit-preview-button"
            >
              Sair da Pré-visualização
            </button>
          </div>
        )}

        {/* Page content */}
        <main className="p-4 lg:p-6 min-w-0 overflow-x-hidden">
          <BackButton />
          {children}
        </main>
        <FloatingHScroll />
      </div>
      
      {/* Strong notifications floating popup (lead reschedule etc.) */}
      <StrongNotificationPopup />
      <AdminAlertBlockingPopup />
      <PendenciaBlockingPopup />
      <LeadAssignBlockingPopup />
      <LeadsNova72hBlockingPopup />
      <AnnouncementPopup />
      <AvatarReminder />
      <ChatDock unread={chatUnread} />
      <PushEnableBanner />
      <PushPrefsDialog open={pushPrefsOpen} onOpenChange={setPushPrefsOpen} />
      <QuickReassignDialog leadIds={reassignIds} onClose={() => setReassignIds(null)} />
      <RecognitionBlockingPopup />
      <SendAlertModal open={sendAlertOpen} onOpenChange={setSendAlertOpen} />
      <SendRecognitionModal open={sendRecognitionOpen} onOpenChange={setSendRecognitionOpen} />
    </div>
  );
};
